import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { SidebarProvider, useSidebar } from "../../components/ui/Sidebar";
import Sidebar from "../../components/ui/Sidebar";
import Button from "components/ui/Button";
import StatCard from "components/ui/StatCard";
import Icon from "components/AppIcon";

import PaymentTable from "./components/PaymentTable";
import LoadingSkeleton from "./components/LoadingSkeleton";
import ModeFilterTabs from "./components/ModeFilterTabs";
import AddPaymentModal from "./components/AddPaymentModal";

import { useGetPayments } from "./useGetPayments";
import { useCreatePayment } from "./useCreatePayment";
import { useUpdatePayment } from "./useUpdatePayment";
import { useDeletePayment } from "./useDeletePayment";
import { useVoidPayment } from "./useVoidPayment";
import { useGetPaymentClients } from "./useGetPaymentClients";
import { useApplyClientCredit } from "./useApplyClientCredit";

import {
  sumAmount,
  countThisMonth,
  countByMode,
  findTopMode,
} from "./logic/stats";

const PAYMENT_MODES = [
  "Bank Deposit",
  "Cash",
  "Cheque",
  "Credit Card",
  "Debit Card",
  "Demand Draft",
  "Online Payment Gateway",
  "Online Transfer",
  "PayPal",
  "Wire Transfer",
];

const PaymentManagementContent = () => {
  const { isCollapsed } = useSidebar();
  const { t } = useTranslation("pages");

  const { data: payments = [], isLoading, isError, error } = useGetPayments();

  const { data: paymentClients = [] } = useGetPaymentClients();

  const createPaymentMutation = useCreatePayment();
  const updatePaymentMutation = useUpdatePayment();
  const deletePaymentMutation = useDeletePayment();
  const voidPaymentMutation = useVoidPayment();
  const applyClientCreditMutation = useApplyClientCredit();

  const [searchTerm, setSearchTerm] = useState("");
  const [activeModeFilter, setActiveModeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);

  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeModeFilter]);

  const filtered = payments.filter((payment) => {
    if (
      activeModeFilter !== "all" &&
      payment.paymentMode !== activeModeFilter
    ) {
      return false;
    }

    if (!searchTerm.trim()) return true;

    const term = searchTerm.toLowerCase();

    return (
      payment.clientName?.toLowerCase().includes(term) ||
      payment.reference?.toLowerCase().includes(term) ||
      payment.comments?.toLowerCase().includes(term) ||
      payment.amount?.toString().includes(term)
    );
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const totalReceived = sumAmount(payments);
  const thisMonth = countThisMonth(payments);
  const modeCounts = countByMode(payments, PAYMENT_MODES);
  const topMode = findTopMode(modeCounts);

  const stats = [
    {
      label: t("payments.stats.totalPayments"),
      value: payments.length,
      icon: "Receipt",
      color: "text-blue-600 bg-blue-100 dark:bg-blue-900/20 dark:text-blue-400",
    },
    {
      label: t("payments.stats.totalReceived"),
      value: `SR ${totalReceived.toLocaleString("en-SA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
      icon: "TrendingUp",
      color:
        "text-green-600 bg-green-100 dark:bg-green-900/20 dark:text-green-400",
    },
    {
      label: t("payments.stats.thisMonth"),
      value: thisMonth,
      icon: "Calendar",
      color: "text-primary bg-primary/10",
    },
    {
      label: t("payments.stats.topMode"),
      value: t(`paymentModes.${topMode}`, { defaultValue: topMode }),
      icon: "Wallet",
      color:
        "text-orange-500 bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400",
    },
  ];

  const handleOpenCreate = () => {
    setEditingPayment(null);
    setIsPaymentModalOpen(true);
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setIsPaymentModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditingPayment(null);
    setIsPaymentModalOpen(false);
  };

  const handleSavePayment = async (payload) => {
    try {
      if (payload.id) {
        await updatePaymentMutation.mutateAsync({
          id: payload.id,
          body: payload,
        });

        toast.success(t("payments.paymentUpdated"));
        handleCloseModal();
        return;
      }

      const creditApplications = Array.isArray(payload.creditApplications)
        ? payload.creditApplications.filter(
            (item) => item.invoiceId && Number(item.amount || 0) > 0,
          )
        : [];

      const paymentPayload = { ...payload };
      delete paymentPayload.creditApplications;

      for (const application of creditApplications) {
        await applyClientCreditMutation.mutateAsync({
          clientId: paymentPayload.clientId,
          payload: {
            invoiceId: application.invoiceId,
            amount: Number(application.amount),
            applicationDate: paymentPayload.paymentDate,
            reference: paymentPayload.reference || t("payments.creditApplied"),
            notes: paymentPayload.comments || "",
          },
        });
      }

      const paymentAmount = Number(paymentPayload.amount || 0);

      if (paymentAmount > 0) {
        await createPaymentMutation.mutateAsync(paymentPayload);
      }

      if (creditApplications.length > 0 && paymentAmount > 0) {
        toast.success(
          t("payments.creditAppliedAndRecorded"),
        );
      } else if (creditApplications.length > 0) {
        toast.success(t("payments.creditApplied"));
      } else {
        toast.success(t("payments.paymentRecorded"));
      }

      handleCloseModal();
    } catch (error) {
      toast.error(error?.message || t("payments.failedSave"));
    }
  };

  const handleDeletePayment = async (paymentId) => {
    try {
      await deletePaymentMutation.mutateAsync(paymentId);
      toast.success(t("payments.paymentDeleted"));
    } catch (error) {
      toast.error(error?.message || t("payments.failedDelete"));
    }
  };

  const handleVoidPayment = async (
    paymentId,
    reason = t("payments.voidReason"),
  ) => {
    try {
      await voidPaymentMutation.mutateAsync({
        id: paymentId,
        reason,
      });

      toast.success(t("payments.paymentVoided"));
    } catch (error) {
      toast.error(error?.message || t("payments.failedVoid"));
    }
  };

  const isSaving =
    createPaymentMutation.isPending ||
    updatePaymentMutation.isPending ||
    applyClientCreditMutation.isPending;

  if (isLoading) return <LoadingSkeleton isCollapsed={isCollapsed} />;

  return (
    <div
      className={`transition-all duration-300 ease-in-out ${
        isCollapsed ? "md:ms-16" : "md:ms-64"
      }`}
    >
      <main className="p-4 md:p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {t("payments.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("payments.description")}
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                size="md"
                iconName="Download"
                className="shadow-sm hover:shadow-lg transition-all duration-300"
                onClick={() => toast(t("payments.exportSoon"))}
              >
                {t("actions.export")}
              </Button>

              <Button
                variant="default"
                size="md"
                iconName="Plus"
                iconPosition="left"
                onClick={handleOpenCreate}
              >
                {t("actions.addPayment")}
              </Button>
            </div>
          </div>
        </motion.div>

        {isError && (
          <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-6">
            <p className="text-sm text-red-700 dark:text-red-300">
              {error?.message || t("payments.failedLoad")}
            </p>
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6"
        >
          {stats.map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.08 }}
          className="mb-6"
        >
          <ModeFilterTabs
            modes={PAYMENT_MODES}
            modeCounts={modeCounts}
            totalCount={payments.length}
            active={activeModeFilter}
            onChange={setActiveModeFilter}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="bg-card rounded-xl p-6 shadow-sm border border-border mb-6"
        >
          <div className="relative">
            <Icon
              name="Search"
              size={20}
              className="absolute start-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
            />

            <input
              type="text"
              placeholder={t("payments.searchPlaceholder")}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="w-full ps-10 pe-10 py-3 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
            />

            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={16} />
              </button>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <PaymentTable
            payments={paginated}
            onEdit={handleEdit}
            onDelete={handleDeletePayment}
            onVoid={handleVoidPayment}
            isDeleting={deletePaymentMutation.isPending}
            isVoiding={voidPaymentMutation.isPending}
          />
        </motion.div>

        {filtered.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-xl p-12 text-center shadow-sm border border-border mt-6"
          >
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Icon name="Wallet" size={32} className="text-primary" />
            </div>

            <h3 className="text-lg font-semibold text-foreground mb-2">
              {t("payments.emptyTitle")}
            </h3>

            <p className="text-muted-foreground mb-6">
              {searchTerm || activeModeFilter !== "all"
                ? t("common.adjustFiltersOrSearch")
                : t("payments.emptyDescription")}
            </p>

            {!searchTerm && activeModeFilter === "all" && (
              <Button
                iconName="Plus"
                iconPosition="left"
                onClick={handleOpenCreate}
              >
                {t("actions.addPayment")}
              </Button>
            )}
          </motion.div>
        )}

        {totalPages > 1 && (
          <div className="mt-4 px-6 py-4 bg-card rounded-xl border border-border">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {t("common.showingRange", {
                  from: (currentPage - 1) * itemsPerPage + 1,
                  to: Math.min(currentPage * itemsPerPage, filtered.length),
                  total: filtered.length,
                  entity: t("entities.payments"),
                })}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ChevronLeft"
                  disabled={currentPage === 1}
                  onClick={() =>
                    setCurrentPage((page) => Math.max(page - 1, 1))
                  }
                />

                <span className="px-3 py-1 text-sm text-foreground">
                  {t("common.pageOf", { current: currentPage, total: totalPages })}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  iconName="ChevronRight"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((page) => Math.min(page + 1, totalPages))
                  }
                />
              </div>
            </div>
          </div>
        )}

        <AnimatePresence>
          {isPaymentModalOpen && (
            <AddPaymentModal
              existingPayment={editingPayment}
              clients={paymentClients}
              onSave={handleSavePayment}
              onClose={handleCloseModal}
              isSaving={isSaving}
            />
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

const PaymentManagement = () => (
  <SidebarProvider>
    <div className="min-h-screen bg-background">
      <Sidebar />
      <PaymentManagementContent />
    </div>
  </SidebarProvider>
);

export default PaymentManagement;
